import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useCallback, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import isPast from 'date-fns/isPast';
import startOfToday from 'date-fns/startOfToday';
import ArchiveIcon from '@mui/icons-material/Archive';

// @mui
import Grid from '@mui/material/Grid2';
import {
	Box,
	Card,
	Chip,
	Table,
	Stack,
	Paper,
	Button,
	Popover,
	Tooltip,
	TableRow,
	MenuItem,
	NativeSelect,
	TableBody,
	TableCell,
	Container,
	Typography,
	IconButton,
	LinearProgress,
	TableContainer,
	TablePagination,
	ToggleButton,
	ToggleButtonGroup,
} from '@mui/material';

// components
import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import Dialog from '../../../components/dialog';
import { Status, PaymentStatus } from '../../../components/request';
import { ListHead, ListToolbar, type HeadCell } from '../../../components/list';
import { type RequestProps, type Status as RequestStatus, Request } from '../../../models';
import { fDateTime } from '../../../utils/formatTime';
import { useRole } from '../../../hooks/useRole';
import { applySortFilter, getComparator, type Order } from '../../../utils/tableSorter';

import {
	useGetRequestsQuery,
	useDelRequestMutation,
	useUpdateRequestAssessmentDateMutation,
	useUpdateRequestStatusMutation,
	type GetRequestsResponse,
} from '../../../slices/apis/requestApi';

// ----------------------------------------------------------------------

const findRequest = (requests: GetRequestsResponse[], id: number) => {
	return requests.find((request) => Number(request.id) === id);
};

export default function Requests() {
	const { isAdmin, isSupplier, isUser } = useRole();
	const { data = [], isFetching } = useGetRequestsQuery();

	const requests = data as GetRequestsResponse[];

	const [requestEditable, setRequestEditable] = useState(false);
	const [page, setPage] = useState(0);
	const [order, setOrder] = useState<Order>('desc');
	const [orderBy, setOrderBy] = useState<keyof GetRequestsResponse>('createdAt');
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [archivedRequest, setArchivedRequest] = useState(() => ['']);

	const [filterName, setFilterName] = useState('');
	const [selected] = useState<string[]>([]);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const theme = useTheme();
	const navigate = useNavigate();
	const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - requests.length) : 0;
	const filteredRequests = applySortFilter({
		list: requests,
		comparator: getComparator(order, orderBy),
		query: filterName,
		orderBy: 'candidateName',
	});
	const isNotFound = !filteredRequests.length && !!filterName;

	/*
		request deletion
	*/
	const [selectedRequest, setSelectedRequest] = useState<RequestProps | null>(null);
	const [delRequest, { isSuccess: isDelReqSuccess, isLoading: isDelReqLoading }] = useDelRequestMutation();
	const [requestDeleteConfirmDialogOpen, setRequestDeleteConfirmDialogOpen] = useState<boolean>(false);

	/*
		request assessment date editing
	*/
	const [assessmentDateDialogOpen, setAssessmentDateDialogOpen] = useState<boolean>(false);
	const [assessmentDate, setAssessmentDate] = useState<Date | null>(null);
	const [updateAssessmentDate, { isSuccess: isUpdateAssessmentDateSuccess, isLoading: isUpdateAssessmentDateLoading }] =
		useUpdateRequestAssessmentDateMutation();

	const handleOpenAssessmentDateDialog = useCallback(
		(e: MouseEvent<HTMLDivElement>) => {
			const requestId = Number(e.currentTarget.getAttribute('data-request-id'));

			const request = findRequest(requests, requestId);

			if (request) {
				const assessmentDate = request.assessmentDates[0];
				const date = isPast(new Date(assessmentDate)) ? startOfToday() : new Date(assessmentDate);

				setAssessmentDate(date);
				setSelectedRequest(request);
				setAssessmentDateDialogOpen(true);
			}
		},
		[requests],
	);

	const handleNavigate = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			const route = e.currentTarget.getAttribute('data-route');
			if (route) {
				navigate(route);
			}
		},
		[navigate],
	);

	const handleCloseAssessmentDateDialog = () => {
		setAssessmentDateDialogOpen(false);
	};

	const onAssessmentDateChange = (newValue: Date) => {
		setAssessmentDate(newValue);
	};

	const onAssessmentDateUpdate = () => {
		if (selectedRequest?.id) {
			updateAssessmentDate({
				id: selectedRequest.id,
				assessmentDate,
			});
		}
	};

	/*
		request status editing
	*/
	const [statusEditingDialogOpen, setStatusEditingDialogOpen] = useState<boolean>(false);
	const [updateRequestStatus, { isSuccess: isUpdateReqStatusSuccess, isLoading: isUpdateReqStatusLoading }] =
		useUpdateRequestStatusMutation();

	let statusOptions = [] as RequestStatus[];

	if (isAdmin) {
		statusOptions = Request.getAllowedEditableStatusAdmin();
	} else if (isSupplier) {
		statusOptions = Request.getAllowedEditableStatusSupplier();
	}

	const handleOpenStatusEditingDialog = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const requestId = Number(e.currentTarget.getAttribute('data-correlation-id'));

			const request = findRequest(requests, requestId);

			if (request) {
				if (Request.isStatusEditingAllowed(request.status)) {
					setStatusEditingDialogOpen(true);
					setSelectedRequest(request);
				}
			}
		},
		[requests],
	);

	const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		if (!statusOptions.includes(e.target.value as RequestStatus)) {
			return;
		}

		setSelectedRequest({
			...selectedRequest,
			status: e.target.value,
		} as RequestProps);
	};

	const onStatusUpdate = () => {
		if (selectedRequest) {
			const { id, status } = selectedRequest;
			updateRequestStatus({
				id,
				status,
			});
		}
	};

	const handleCloseStatusEditingDialog = () => {
		setStatusEditingDialogOpen(false);
	};

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		const requestId = Number(event.currentTarget.getAttribute('data-request-id'));

		const request = findRequest(requests, requestId);

		if (request) {
			setAnchorEl(event.currentTarget);
			setSelectedRequest(request);
			setRequestEditable(Request.isEditable(request.status));
		}
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof RequestProps) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => {
		setPage(newPage);
		setTimeout(() => {
			window.scroll({ top: 0, behavior: 'smooth' });
		}, 0);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setRowsPerPage(Number.parseInt(event.target.value, 10));
	};

	const handleFilterByName = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setFilterName(event.target.value);
	};

	const handleEditRequest = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/requests/${selectedRequest.id}/edit`);
		}
	};

	const handleOpenRequestDetail = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/requests/${selectedRequest.id}/view`);
		}
	};

	/*
		del request
	*/
	const handleDeleteRequest = () => {
		setAnchorEl(null);
		setRequestDeleteConfirmDialogOpen(true);
	};

	const handleCloseRequestDeleteConfirmDialog = () => {
		setRequestDeleteConfirmDialogOpen(false);
	};

	const onRequestDeleteConfirm = () => {
		if (selectedRequest?.id) {
			delRequest(selectedRequest.id);
		}
	};

	/*
		handle archived switch
	*/
	const handleArchivedSwitch = () => {
		if (archivedRequest.includes('archived')) {
			setArchivedRequest(['']);
		} else {
			setArchivedRequest(['archived']);
		}
	};

	const filterArchivedRequests = (request: RequestProps) => {
		if (archivedRequest.includes('archived')) {
			return true;
		}

		return !request.isArchived;
	};

	useEffect(() => {
		if (isUpdateAssessmentDateSuccess) {
			setAssessmentDateDialogOpen(false);
			navigate('/dashboard/requests');
		}

		if (isDelReqSuccess) {
			setRequestDeleteConfirmDialogOpen(false);
		}

		if (isUpdateReqStatusSuccess) {
			setStatusEditingDialogOpen(false);
			navigate('/dashboard/requests');
		}
	}, [isDelReqSuccess, isUpdateReqStatusSuccess, isUpdateAssessmentDateSuccess, navigate]);

	const TABLE_HEAD: HeadCell[] = isAdmin
		? [
				{ id: 'id', label: 'ID', align: 'left' },
				{ id: 'name', label: 'Name', align: 'left' },
				{ id: 'phone', label: 'Phone', align: 'left' },
				{ id: 'submitted_by', label: 'Submitted By', align: 'left' },
				{ id: 'employer', label: 'Employer', align: 'left' },
				{ id: 'testing_centre', label: 'Testing Centre', align: 'left' },
				{ id: 'assessmentDate', label: 'Assessment Date', align: 'left' },
				{ id: 'payment_status', label: 'Payment Status', align: 'center' },
				{ id: 'status', label: 'Status', align: 'center' },
				{ id: 'edit', label: '', align: 'left', disableSort: true },
			]
		: [
				{ id: 'id', label: 'ID', align: 'left' },
				{ id: 'name', label: 'Name', align: 'left' },
				{ id: 'phone', label: 'Phone', align: 'left' },
				{ id: 'employer', label: 'Employer', align: 'left' },
				{ id: 'testing_centre', label: 'Testing Centre', align: 'left' },
				{ id: 'assessmentDate', label: 'Assessment Date', align: 'left' },
				{ id: 'payment_status', label: 'Payment Status', align: 'center' },
				{ id: 'status', label: 'Status', align: 'left' },
				{ id: 'edit', label: '', align: 'left', disableSort: true },
			];

	return (
		<>
			<Helmet>
				<title> Requests - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Container maxWidth={false} disableGutters sx={{ px: 1, width: '100%' }}>
				<Stack spacing={4} mb={4}>
					{!isSupplier && !isFetching && (
						<Box>
							<Button
								variant="contained"
								data-route="/dashboard/requests/new"
								onClick={handleNavigate}
								startIcon={<Iconify icon="eva:plus-fill" />}
							>
								New Single Request
							</Button>
						</Box>
					)}
				</Stack>

				{isFetching ? (
					<LinearProgress />
				) : (
					<>
						<Card
							sx={{
								width: '100%',
								overflow: 'auto',
							}}
						>
							<ListToolbar
								numSelected={selected.length}
								filterName={filterName}
								onFilterName={handleFilterByName}
								placeHolderText={'Candidate name...'}
								{...(isAdmin && {
									rightContent: (
										<ToggleButtonGroup
											color="primary"
											value={archivedRequest}
											size="small"
											exclusive
											onChange={handleArchivedSwitch}
										>
											<Tooltip title="Archived Requestssss">
												<ToggleButton value="archived">
													<ArchiveIcon />
												</ToggleButton>
											</Tooltip>
										</ToggleButtonGroup>
									),
								})}
							/>

							<Scrollbar>
								<TableContainer
									sx={{
										minWidth: 1000,
										'@media (max-width: 1400px)': {
											minWidth: 1000,
										},
									}}
								>
									<Table
										sx={{
											'& .MuiTableCell-root': {
												paddingX: 0.75,
												fontSize: '0.8rem',
												'@media (max-width: 1400px)': {
													paddingX: 0.5,
													fontSize: '0.75rem',
												},
											},
											'& .MuiTableCell-head': {
												fontWeight: 600,
											},
										}}
									>
										<ListHead
											order={order}
											orderBy={orderBy}
											headLabel={TABLE_HEAD}
											onRequestSort={handleRequestSort}
										/>
										<TableBody>
											{filteredRequests
												.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
												.filter(filterArchivedRequests)
												.map((request) => {
													const {
														candidateName,
														candidate,
														supplier,
														status,
														assessmentDates,
														id,
														isArchived,
														paymentStatus,
														user,
													} = request;

													const { employer, phone } = candidate;
													const { name: testingCentre } = supplier;
													const assessmentDate = assessmentDates[0];

													let assessmentDateView = null;
													let statusEditingView = null;

													if (isUser) {
														if (assessmentDate) {
															assessmentDateView = (
																<Chip
																	label={fDateTime(assessmentDate)}
																	color="primary"
																	variant="outlined"
																	data-request-id={id}
																	onClick={handleOpenAssessmentDateDialog}
																	disabled={isUser || Request.disableAssessmentSchedulling(status)}
																/>
															);
														} else {
															assessmentDateView = <Chip label="PENDING" color="primary" variant="outlined"/>;
														}
													} else {
														assessmentDateView = (
															<Chip
																label={assessmentDate ? fDateTime(assessmentDate) : 'Select a Date'}
																color="primary"
																variant="outlined"
																data-request-id={id}
																onClick={handleOpenAssessmentDateDialog}
																disabled={isUser || Request.disableAssessmentSchedulling(status)}
															/>
														);
													}

													if (isUser) {
														statusEditingView = <Status status={status} />;
													} else {
														statusEditingView = (
															<Status
																status={status}
																clickable
																correlationId={id}
																onClick={handleOpenStatusEditingDialog}
															/>
														);
													}

													return (
														<TableRow
															hover
															key={id}
															tabIndex={-1}
															selected={selected.indexOf(candidateName) !== -1}
															sx={isArchived ? { backgroundColor: theme.palette.grey['400'] } : {}}
														>
															<TableCell align="left">
																<Typography variant="subtitle2">{id}</Typography>
															</TableCell>
															<TableCell align="left">{candidateName}</TableCell>
															<TableCell align="left">{phone}</TableCell>
															{isAdmin && <TableCell align="left">{`${user.firstname} ${user.lastname}`}</TableCell>}
															<TableCell align="left">{employer}</TableCell>
															<TableCell align="left">{testingCentre}</TableCell>
															<TableCell
																align="left"
																sx={{
																	':hover': {
																		cursor: 'pointer',
																		color: theme.palette.primary.main,
																	},
																}}
															>
																{assessmentDateView}
															</TableCell>
															<TableCell align="center">
																<PaymentStatus paymentStatus={paymentStatus || 'Unknown'} />
															</TableCell>
															<TableCell>{statusEditingView}</TableCell>
															<TableCell>
																<IconButton size="large" color="inherit" data-request-id={id} onClick={handleOpenMenu}>
																	<Iconify icon={'eva:more-vertical-fill'} />
																</IconButton>
															</TableCell>
														</TableRow>
													);
												})}
											{emptyRows > 0 && (
												<TableRow style={{ height: 53 * emptyRows }}>
													<TableCell colSpan={9} />
												</TableRow>
											)}
										</TableBody>

										{isNotFound && (
											<TableBody>
												<TableRow>
													<TableCell align="center" colSpan={9} sx={{ py: 3 }}>
														<Paper
															sx={{
																textAlign: 'center',
															}}
														>
															<Typography variant="h6" paragraph>
																Not found
															</Typography>

															<Typography variant="body2">
																No results found for &nbsp;
																<strong>&quot;{filterName}&quot;</strong>.
																<br /> Check your typos or use complete words
															</Typography>
														</Paper>
													</TableCell>
												</TableRow>
											</TableBody>
										)}
									</Table>
								</TableContainer>
							</Scrollbar>

							<TablePagination
								rowsPerPageOptions={[10, 20, 50, 100]}
								component="div"
								count={requests.length}
								rowsPerPage={rowsPerPage}
								page={page}
								onPageChange={handleChangePage}
								onRowsPerPageChange={handleChangeRowsPerPage}
							/>
						</Card>

						{/* request menu options popover */}
						<Popover
							open={Boolean(anchorEl)}
							onClose={handleCloseMenu}
							anchorEl={anchorEl}
							anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							PaperProps={{
								sx: {
									p: 1,
									width: 140,
									'& .MuiMenuItem-root': {
										px: 1,
										typography: 'body2',
										borderRadius: 0.75,
									},
								},
							}}
						>
							<MenuItem onClick={handleOpenRequestDetail}>
								<Iconify icon={'eva:file-text-outline'} sx={{ mr: 1 }} />
								View
							</MenuItem>

							{isSupplier || !requestEditable ? null : (
								<MenuItem onClick={handleEditRequest}>
									<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
									Edit
								</MenuItem>
							)}

							{/* prevent users deleting their own unfinished requests whether inadvertently or not */}
							{isSupplier || isUser ? null : (
								<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteRequest}>
									<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
									Delete
								</MenuItem>
							)}
						</Popover>

						{/* assessment date editing */}
						<Dialog
							open={Boolean(assessmentDateDialogOpen)}
							onClose={handleCloseAssessmentDateDialog}
							title={`Choose an assessment date/time for request ${selectedRequest?.id}`}
						>
							<Grid container spacing={2} sx={{ justifyContent: 'center', alignItems: 'center' }}>
								<Grid>
									<DateTimePicker
										value={assessmentDate}
										onChange={onAssessmentDateChange}
										ampm
										format={'dd MMM yyyy p'}
										slotProps={{
											// TODO: forced so mui.error css never shows paints field border red
											// when formik is used, we need to handle validation and show error in a proper way
											textField: {
												error: false,
											},
										}}
									/>
								</Grid>
								<Grid>
									<Typography variant="subtitle2" sx={{ color: theme.palette.grey[600] }}>
										By confirming the assessment date/time, a confirmation email will be sent to the candidate. Please
										double check the date/time selected.
									</Typography>
								</Grid>
								<Grid>
									<Button
										loading={isUpdateAssessmentDateLoading}
										variant="outlined"
										color="primary"
										onClick={onAssessmentDateUpdate}
									>
										<span>Confirm</span>
									</Button>
								</Grid>
								<Grid>
									<Button color="error" variant="outlined" onClick={handleCloseAssessmentDateDialog}>
										Cancel
									</Button>
								</Grid>
							</Grid>
						</Dialog>

						{/* status editing */}
						<Dialog
							open={Boolean(statusEditingDialogOpen)}
							onClose={handleCloseStatusEditingDialog}
							title={`Change request ${selectedRequest?.id} status`}
						>
							<Grid container spacing={2}>
								<Grid size={{ md: 12 }}>
									<NativeSelect
										defaultValue={selectedRequest?.status}
										inputProps={{
											name: 'status',
											id: 'uncontrolled-native',
											onChange: onStatusChange,
										}}
									>
										{['--- Please Select ---', ...statusOptions].map((status, index) => (
											<option key={status} value={status}>
												{status}
											</option>
										))}
									</NativeSelect>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button
										loading={isUpdateReqStatusLoading}
										variant="outlined"
										color="primary"
										onClick={onStatusUpdate}
									>
										<span>Confirm</span>
									</Button>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button color="error" variant="outlined" onClick={handleCloseStatusEditingDialog}>
										Cancel
									</Button>
								</Grid>
							</Grid>
						</Dialog>

						{/* request delete confirm */}
						<Dialog
							open={Boolean(requestDeleteConfirmDialogOpen)}
							onClose={handleCloseRequestDeleteConfirmDialog}
							title={'Warning'}
						>
							<Grid container spacing={2}>
								<Grid size={{ md: 12 }}>
									<Typography variant="subtitle2">{`Confirm to delete request ${selectedRequest?.id}`}</Typography>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button loading={isDelReqLoading} variant="outlined" color="primary" onClick={onRequestDeleteConfirm}>
										<span>Confirm</span>
									</Button>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button color="error" variant="outlined" onClick={handleCloseRequestDeleteConfirmDialog}>
										Cancel
									</Button>
								</Grid>
							</Grid>
						</Dialog>
					</>
				)}
			</Container>
		</>
	);
}
